/**
 * AX1.Capital — Environment Validation & Secrets Safety
 *
 * Validates that all required environment variables are present and
 * correctly configured for the current execution mode. In staging and
 * production, missing secrets cause a hard fail at startup rather than
 * silently degrading to demo behavior.
 *
 * @module lib/runtime/envValidation
 */

import { getExecutionMode, type ExecutionMode } from './executionMode';
import { resolveEnvironmentMode } from '@/config/environmentMode';

// ─────────────────────────────────────────────────────────────────────────────
// Env var access helper
// ─────────────────────────────────────────────────────────────────────────────

function readEnv(key: string): string | undefined {
    try {
        // Vite
        if (typeof import.meta !== 'undefined' && import.meta.env) {
            const val = (import.meta.env as Record<string, string | undefined>)[key];
            if (val) return val;
        }
    } catch { /* not in Vite */ }

    try {
        if (typeof process !== 'undefined' && process.env) {
            return process.env[key] ?? undefined;
        }
    } catch { /* not in Node */ }

    return undefined;
}
// ─────────────────────────────────────────────────────────────────────────────
// Env requirement definitions
// ─────────────────────────────────────────────────────────────────────────────

export interface EnvRequirement {
    key: string;
    label: string;
    /** Which modes require this var to be present */
    requiredIn: ExecutionMode[];
    /** Which modes recommend this var (warn-only) */
    recommendedIn?: ExecutionMode[];
    /** Whether this is a secret (should be redacted in logs/UI) */
    secret: boolean;
    /** Category for grouping in diagnostics */
    category: 'core' | 'supabase' | 'capital' | 'notifications' | 'monitoring';
}

const ENV_REQUIREMENTS: EnvRequirement[] = [
    // Core
    { key: 'VITE_APP_MODE', label: 'App Mode', requiredIn: ['staging', 'production'], secret: false, category: 'core' },
    { key: 'VITE_EXECUTION_MODE', label: 'Execution Mode', requiredIn: ['staging', 'production'], secret: false, category: 'core' },
    { key: 'VITE_REQUIRE_MFA', label: 'Require MFA', requiredIn: ['staging', 'production'], secret: false, category: 'core' },

    // Supabase
    { key: 'VITE_SUPABASE_URL', label: 'Supabase URL', requiredIn: ['staging', 'production'], secret: false, category: 'supabase' },
    { key: 'VITE_SUPABASE_PUBLISHABLE_KEY', label: 'Supabase Publishable Key', requiredIn: ['staging', 'production'], secret: false, category: 'supabase' },

    // Capital API
    { key: 'VITE_CAPITAL_MODE', label: 'Capital Mode', requiredIn: [], recommendedIn: ['staging', 'production'], secret: false, category: 'capital' },

    // Notifications
    { key: 'VITE_CAPITAL_EMAIL_ENABLED', label: 'Server Email Delivery', requiredIn: [], recommendedIn: ['production'], secret: false, category: 'notifications' },
    { key: 'VITE_CAPITAL_WEBHOOK_ENABLED', label: 'Server Webhook Delivery', requiredIn: [], recommendedIn: ['production'], secret: false, category: 'notifications' },

    // Monitoring
    { key: 'VITE_SENTRY_DSN', label: 'Sentry DSN', requiredIn: [], recommendedIn: ['staging', 'production'], secret: true, category: 'monitoring' },

    // Go-live controls
    { key: 'VITE_LIVE_CAPITAL_EXECUTION_ENABLED', label: 'Live Execution Enabled', requiredIn: [], recommendedIn: ['production'], secret: false, category: 'capital' },
    { key: 'VITE_CAPITAL_LIVE_WORKSPACES', label: 'Live Workspace Allowlist', requiredIn: [], recommendedIn: ['production'], secret: false, category: 'capital' },
];

const FORBIDDEN_BROWSER_SECRET_KEYS = [
    'VITE_CAPITAL_SECRET',
    'VITE_CAPITAL_WEBHOOK_SECRET',
] as const;

const FORBIDDEN_BROWSER_CALENDAR_OAUTH_KEYS = [
    'VITE_GOOGLE_CLIENT_ID',
    'VITE_GOOGLE_REDIRECT_URI',
    'VITE_MICROSOFT_CLIENT_ID',
    'VITE_MICROSOFT_TENANT_ID',
    'VITE_MICROSOFT_REDIRECT_URI',
] as const;

// ─────────────────────────────────────────────────────────────────────────────
// Validation result
// ─────────────────────────────────────────────────────────────────────────────

export type EnvCheckStatus = 'ok' | 'missing_required' | 'missing_recommended' | 'not_applicable';

export interface EnvCheckResult {
    key: string;
    label: string;
    category: string;
    status: EnvCheckStatus;
    present: boolean;
    secret: boolean;
    /** Redacted value (first 4 chars + ***) or '<not set>' */
    redactedValue: string;
}

export interface EnvValidationResult {
    mode: ExecutionMode;
    valid: boolean;
    errors: string[];
    warnings: string[];
    checks: EnvCheckResult[];
}

// ─────────────────────────────────────────────────────────────────────────────
// Validation logic
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Validate all environment requirements for the given execution mode.
 */
export function validateEnvironment(mode: ExecutionMode = getExecutionMode()): EnvValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];
    const checks: EnvCheckResult[] = [];

    for (const req of ENV_REQUIREMENTS) {
        const raw = readEnv(req.key);
        const present = !!raw && raw.trim().length > 0;
        const isRequired = req.requiredIn.includes(mode);
        const isRecommended = req.recommendedIn?.includes(mode) ?? false;

        let status: EnvCheckStatus;

        if (isRequired && !present) {
            status = 'missing_required';
            errors.push(`[REQUIRED] ${req.label} (${req.key}) is missing in ${mode} mode.`);
        } else if (isRecommended && !present) {
            status = 'missing_recommended';
            warnings.push(`[RECOMMENDED] ${req.label} (${req.key}) is not set in ${mode} mode.`);
        } else if (!isRequired && !isRecommended) {
            status = present ? 'ok' : 'not_applicable';
        } else {
            status = 'ok';
        }

        checks.push({
            key: req.key,
            label: req.label,
            category: req.category,
            status,
            present,
            secret: req.secret,
            redactedValue: present ? redactValue(raw!, req.secret) : '<not set>',
        });
    }

    applyPolicyChecks(mode, errors);

    return {
        mode,
        valid: errors.length === 0,
        errors,
        warnings,
        checks,
    };
}

/**
 * Assert that the environment is valid for the current execution mode.
 * Throws in staging/production if required vars are missing.
 * Only warns in demo mode.
 */
export function assertEnvironmentValid(mode: ExecutionMode = getExecutionMode()): void {
    const result = validateEnvironment(mode);

    // In demo mode, just log warnings
    if (mode === 'demo') {
        for (const w of result.warnings) {
            console.info(`[envValidation] ${w}`);
        }
        return;
    }

    // In staging/production, missing required vars are fatal
    if (!result.valid) {
        const msg = [
            `[envValidation] FATAL: Environment validation failed for ${mode} mode.`,
            ...result.errors,
        ].join('\n');

        console.error(msg);

        // Hard fail — throw to prevent the app from running in a broken state
        throw new Error(msg);
    }

    // Log warnings for recommended-but-missing vars
    for (const w of result.warnings) {
        console.warn(`[envValidation] ${w}`);
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Redaction
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Redact a value for safe display. Non-secret values are shown in full.
 * Secret values show only the first 4 characters followed by '***'.
 */
export function redactValue(value: string, isSecret: boolean): string {
    if (!isSecret) return value;
    if (value.length <= 4) return '***';
    return `${value.slice(0, 4)}***`;
}

/**
 * Redact all known secret env vars from a string (e.g., log output or
 * error messages). This is a safety net — code should never log secrets
 * in the first place, but this catches accidental leaks.
 */
export function redactSecrets(text: string): string {
    let result = text;
    for (const req of ENV_REQUIREMENTS) {
        if (!req.secret) continue;
        const raw = readEnv(req.key);
        if (raw && raw.length > 4) {
            // Only redact if the secret is long enough to be meaningful
            result = result.split(raw).join(`[REDACTED:${req.key}]`);
        }
    }
    for (const key of FORBIDDEN_BROWSER_SECRET_KEYS) {
        const raw = readEnv(key);
        if (raw && raw.length > 4) {
            result = result.split(raw).join(`[REDACTED:${key}]`);
        }
    }
    return result;
}

/**
 * Get the list of all env requirements (for admin diagnostics UI).
 */
export function getEnvRequirements(): readonly EnvRequirement[] {
    return ENV_REQUIREMENTS;
}

function applyPolicyChecks(
    mode: ExecutionMode,
    errors: string[]
): void {
    const requireMfa = readBooleanEnv('VITE_REQUIRE_MFA');
    const explicitDemoMode = readBooleanEnv('VITE_DEMO_MODE');
    const hasFrontendCapitalSecret = Boolean(readEnv('VITE_CAPITAL_SECRET')?.trim());
    const hasBrowserCapitalRemoteConfig = Boolean(
        readEnv('VITE_CAPITAL_API_URL')?.trim()
        || readEnv('VITE_CAPITAL_KEY_ID')?.trim()
        || readEnv('VITE_CAPITAL_WORKSPACE_ID')?.trim(),
    );
    const hasFrontendWebhookConfig = Boolean(
        readEnv('VITE_CAPITAL_WEBHOOK_SECRET')?.trim()
        || readEnv('VITE_CAPITAL_WEBHOOK_URL')?.trim(),
    );
    const hasFrontendEmailConfig = Boolean(
        readEnv('VITE_CAPITAL_EMAIL_ENDPOINT')?.trim()
        || readEnv('VITE_CAPITAL_EMAIL_FROM')?.trim(),
    );
    const hasBrowserCalendarOAuthConfig = FORBIDDEN_BROWSER_CALENDAR_OAUTH_KEYS.some(
        (key) => Boolean(readEnv(key)?.trim()),
    );
    const modeResolution = resolveEnvironmentMode({
        appMode: readEnv('VITE_APP_MODE'),
        executionMode: readEnv('VITE_EXECUTION_MODE') ?? readEnv('NEXT_PUBLIC_EXECUTION_MODE'),
        nodeEnv: readEnv('NODE_ENV'),
    });

    if (modeResolution.configurationError) {
        errors.push(`[POLICY] ${modeResolution.configurationError}`);
    }

    if (modeResolution.executionMode !== mode) {
        errors.push(
            `[POLICY] Requested ${mode} validation does not match the configured `
            + `${modeResolution.executionMode} execution mode.`,
        );
    }

    if (mode === 'production') {
        if (requireMfa !== true) {
            errors.push('[POLICY] Production deployments must set VITE_REQUIRE_MFA=true.');
        }

        if (readEnv('VITE_EXECUTION_MODE')?.trim().toLowerCase() !== 'production') {
            errors.push('[POLICY] Production deployments must set VITE_EXECUTION_MODE=production explicitly.');
        }

        if (modeResolution.appMode === 'demo' || explicitDemoMode === true) {
            errors.push('[POLICY] Demo mode flags must be disabled in production deployments.');
        }

        if (hasFrontendCapitalSecret) {
            errors.push('[POLICY] VITE_CAPITAL_SECRET exposes a shared secret to the browser. Move Capital signing to a server-side secret.');
        }
        if (hasBrowserCapitalRemoteConfig) {
            errors.push('[POLICY] Browser Capital HTTP configuration is forbidden. AX1 v1 uses the governed Supabase/RPC contract.');
        }
        if (hasFrontendWebhookConfig) {
            errors.push('[POLICY] Browser webhook URLs and secrets are forbidden. Configure CAPITAL_WEBHOOK_URL and CAPITAL_WEBHOOK_SECRET only on the Edge Function.');
        }
        if (hasFrontendEmailConfig) {
            errors.push('[POLICY] Browser email endpoints and sender settings are forbidden. Configure the authenticated Edge Function and email service instead.');
        }
        if (hasBrowserCalendarOAuthConfig) {
            errors.push('[POLICY] Browser calendar OAuth configuration is forbidden until tokens and provider API calls use the server-managed integration.');
        }
    }

    if (mode === 'staging') {
        if (requireMfa !== true) {
            errors.push('[POLICY] Staging must mirror production and set VITE_REQUIRE_MFA=true.');
        }

        if (explicitDemoMode === true) {
            errors.push('[POLICY] Demo mode flags must be disabled in staging deployments.');
        }

        if (hasFrontendCapitalSecret) {
            errors.push('[POLICY] VITE_CAPITAL_SECRET is browser-exposed. Use a server-side signing path in staging and production.');
        }
        if (hasBrowserCapitalRemoteConfig) {
            errors.push('[POLICY] Browser Capital HTTP configuration is forbidden. AX1 v1 uses the governed Supabase/RPC contract.');
        }
        if (hasFrontendWebhookConfig) {
            errors.push('[POLICY] Browser webhook URLs and secrets are forbidden. Configure the server-side Edge Function instead.');
        }
        if (hasFrontendEmailConfig) {
            errors.push('[POLICY] Browser email endpoints and sender settings are forbidden. Configure server-side email delivery instead.');
        }
        if (hasBrowserCalendarOAuthConfig) {
            errors.push('[POLICY] Browser calendar OAuth configuration is forbidden until tokens and provider API calls use the server-managed integration.');
        }
    }
}

function readBooleanEnv(key: string): boolean | null {
    const value = readEnv(key)?.trim().toLowerCase();

    if (!value) return null;
    if (['true', '1', 'yes', 'on'].includes(value)) return true;
    if (['false', '0', 'no', 'off'].includes(value)) return false;

    return null;
}
